<template>
    <div class="settings-content">
      <!-- Header (samo kad je showHeader true) -->
      <div v-if="showHeader" class="content-header q-pa-md">
        <div class="row items-center">
          <q-btn 
            v-if="showBackButton"
            flat 
            round 
            icon="arrow_back" 
            @click="$emit('back')"
            class="q-mr-sm"
          />
          <div class="text-h5 text-weight-medium text-grey-8">{{$t('Settings')}}</div>
          <q-space />
          <q-btn 
            v-if="showCloseButton"
            flat 
            round 
            icon="close" 
            @click="$emit('close')"
          />
        </div>
      </div>
  
      <!-- Content List -->
      <div class="q-pa-md">
        <q-list separator>
          <!-- Account - clickable item (samo za logged in) -->
        <!-- <q-item 
          v-if="isAuthenticated"
          clickable 
          v-ripple
          @click="handleAccountClick"
        >
          <q-item-section avatar>
            <q-icon name="person" color="grey-6" size="md" />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-grey-8 text-weight-medium">
              {{$t('Account')}}
            </q-item-label>
            <q-item-label caption class="text-grey-6">
              {{$t('Profile and security settings')}}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-icon name="chevron_right" color="grey-4" />
          </q-item-section>
        </q-item> -->
  
          <!-- Language -->
          <q-item 
            clickable 
            v-ripple
            @click="handleLanguageClick"
          >
            <q-item-section avatar>
              <q-icon name="language" color="grey-6" size="md" />
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-grey-8 text-weight-medium">
                {{ $t('Language') }}
              </q-item-label>
              <q-item-label caption class="text-grey-6">
                {{ currentLanguageLabel }}
              </q-item-label>
            </q-item-section>
             <q-item-section side>
              <q-icon :name="showLanguageExpanded ? 'expand_less' : 'expand_more'" />
            </q-item-section>
          </q-item>

          <!-- Expanded language list -->
          <q-slide-transition>
            <div v-show="showLanguageExpanded" class="q-pl-xl q-pr-md">
              <LanguageSelector @locale-changed="handleLocaleChange" />
            </div>
          </q-slide-transition>
    
  
          <!-- Dyslexic Font -->
          <q-item>
            <q-item-section avatar>
              <q-icon color="grey-6" size="md">
                <span style="font-size: 24px; font-family: OpenDyslexic">a</span>
              </q-icon>
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-grey-8 text-weight-medium">
                {{$t('Dyslexic Font') }}
              </q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-toggle
                v-model="dyslexicFont"
                color="positive"
              />
            </q-item-section>
          </q-item>
  
          <!-- Font Size -->
          <q-item>
            <q-item-section avatar>
              <q-icon name="format_size" color="grey-6" size="md" />
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-grey-8 text-weight-medium q-mb-sm">
                {{$t('Font Size')}}
              </q-item-label>
              <q-slider
                v-model="fontSize"
                :min="1"
                :max="4"
                :step="1"
                color="positive"
                track-color="grey-4"
                thumb-color="positive"
                class="q-mt-sm"
              />
            </q-item-section>
          </q-item>
  
          <!-- Dark Mode -->
          <q-item>
            <q-item-section avatar>
              <q-icon name="palette" color="grey-6" size="md" />
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-grey-8 text-weight-medium">
                {{$t('Dark theme')}}
              </q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-toggle
                v-model="darkMode"
                color="positive"
              />
            </q-item-section>
          </q-item>
        </q-list>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import { useQuasar } from 'quasar'
  import { useAuth } from '@/common/composables/useAuth'
  import { useLanguage } from '@/common/composables/useLanguage'
  import LanguageSelector from '@/common/components/app/lang-selector.vue'
  
  const showLanguageExpanded = ref(false)
  
  const props = defineProps({
    showHeader: {
      type: Boolean,
      default: false
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    showCloseButton: {
      type: Boolean,
      default: false
    }
  })
  
  
  // const emit = defineEmits(['back', 'close', 'account-click', 'language-click', 'settings-change'])
  const emit = defineEmits(['back', 'close', 'item-click', 'settings-change'])
  
  const $q = useQuasar()
  const { isAuthenticated } = useAuth()
  const { currentLocale, localeOptions, changeLocale, initializeLanguage   } = useLanguage()

  const isUserLoggedIn = computed(() =>  isAuthenticated ?? false)
  
  // Settings state
  const dyslexicFont = ref(false)
  const fontSize = ref(1)
  
  // Dark mode kao computed property - sinkroniziran sa Quasar dark mode
  const darkMode = computed({
    get: () => $q.dark.isActive,
    set: (val) => {
      $q.dark.set(val)
      localStorage.setItem("dark", val)
      emit('settings-change', { type: 'darkMode', value: val })
    }
  })
  
  // Font scaling faktori
  const fontScales = {
    1: 1,      // 100% - normalno
    2: 1.125,  // 112.5%
    3: 1.25,   // 125%
    4: 1.375   // 137.5%
  }
  
  // Funkcija za primjenu font size-a
  const applyFontSize = (level) => {
    const scale = fontScales[level]
      // Ukloni stare klase
    for (let i = 1; i <= 4; i++) {
      document.body.classList.remove(`font-scale-${i}`)
    }
    // Dodaj novu klasu
    document.body.classList.add(`font-scale-${level}`)
  }
  
  // Funkcija za dyslexic font
  const applyDyslexicFont = (enabled) => {
    if (enabled) {
      document.body.classList.add('dyslexic')
    } else {
      document.body.classList.remove('dyslexic')
    }
  }
  
  // Load settings from localStorage
  onMounted(() => {
    dyslexicFont.value = localStorage.getItem("dyslexia") === "true"
    fontSize.value = parseInt(localStorage.getItem("zoomlevel") ?? 1)
    
    // Za dark mode provjeri localStorage
    const savedDark = localStorage.getItem("dark")
    if (savedDark !== null) {
      $q.dark.set(savedDark === "true")
    }
    
    // Primijeni početne postavke
    applyFontSize(fontSize.value)
    applyDyslexicFont(dyslexicFont.value)
  })
  
  // Watch za dyslexic font
  watch(dyslexicFont, (val) => {
    applyDyslexicFont(val)
    localStorage.setItem("dyslexia", val)
    emit('settings-change', { type: 'dyslexicFont', value: val })
  })
  
  // Watch za font size
  watch(fontSize, (val) => {
    applyFontSize(val)
    localStorage.setItem("zoomlevel", val)
    emit('settings-change', { type: 'fontSize', value: val })
  })
  
  const handleAccountClick = () => {
    emit('item-click', 'account')
  }
  
  // const handleLanguageClick = () => {
  //   emit('item-click', 'language')
  // }

  const handleLanguageClick = () => {
    showLanguageExpanded.value = !showLanguageExpanded.value  // ← Toggle
  }

  const handleLocaleChange = async (locale) => {
    await changeLocale(locale)
    showLanguageExpanded.value = false
  }


  const currentLanguageLabel = computed(() => {
    const locale = localeOptions.value.find(l => l.value === currentLocale.value)
    return locale ? locale.label : 'English (US)'
  })
</script>

  <style lang="scss">
    @import "@/assets/styles/accessibility.scss"; 


    // Font scaling - forsiraj na sve elemente
    body.font-scale-1 { font-size: 100%; }
    body.font-scale-2 { 
      font-size: 112.5%; 
    }
    body.font-scale-3 { 
      font-size: 125%; 
    }
    body.font-scale-4 { 
      font-size: 137.5%; 
    }
  </style>
  
  <style lang="scss" scoped>
    .text-grey-8 {
      body.body--dark & {
        color: white !important;
      }
    }
  .settings-content {
    .content-header {
      border-bottom: 1px solid #e0e0e0;
    }
  }
  </style>